import {
  useAlertCallback,
  useComponentTimer,
  useIsLargeDevice,
} from '@jobseeker/lotus/hooks';
import {audioWave} from '@jobseeker/lotus/images';
import {Box, LinearProgress, Typography, styled, useTheme} from '@mui/material';
import {useState, useEffect, useRef, useMemo} from 'react';
import {VoiceVisualizer} from 'react-voice-visualizer';
import useVoiceVisualizer from './useVoiceVisualizer';
import {Image} from '@jobseeker/lotus/components/Image';
import ButtonV2 from '@jobseeker/lotus/elements/ButtonV2';
import {BsCameraVideo} from 'react-icons/bs';
import {FaPause, FaPlay, FaRegStopCircle} from 'react-icons/fa';
import {FiUpload} from 'react-icons/fi';
import {VscDebugRestart} from 'react-icons/vsc';
import {SurveyCheckGreen, audio as audioIcon} from '@jobseeker/lotus/icons';
import {RecordingOverlay, IconContainer} from '../common';
import {getRefTagAndOrigin, handleTracking} from '@jobseeker/common-core/utils';
const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

const Button = styled(ButtonV2)(({theme}) => ({
  padding: theme.spacing(1, 3),
}));

export const AudioRecorder = (props: any) => {
  const {isMinimalist, setMinimalistView} = props;
  const isLarge = useIsLargeDevice();

  const theme = useTheme();
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setPlaying] = useState(false);
  const [minimalistViewCallback, setMinimalistViewCallback] = useState<any>();
  const [recordDuration, setRecordDuration] = useState(0);
  const recordDurationRef = useRef<any>();
  const [disableClick, setDisableClick] = useState(true);

  const startRecordCounter = () => {
    recordDurationRef.current = setInterval(() => {
      setRecordDuration(count => count + 1);
    }, 1000);
  };

  useEffect(() => {
    const handleSwitchEvent = (event: any) => {
      if (event?.detail?.delete) {
        handleRestartClick();
      }
      // if (recordedBlob && props.isUploaded) {
      //   stopVideoStream();
      // }
    };
    window.addEventListener('stepSwitchEvent', handleSwitchEvent);
    return () =>
      window.removeEventListener('stepSwitchEvent', handleSwitchEvent);
  }, []);

  useEffect(() => {
    if (!isRecording) {
      clearInterval(recordDurationRef.current);
      recordDurationRef.current = null;
    }
    if (isRecording && !recordDurationRef.current) {
      startRecordCounter();
    }
    if (recordDuration >= 60) {
      handleFinishClick();
    }
    if (recordDuration > 5) {
      setDisableClick(false);
    }
  }, [isRecording, recordDuration]);

  const formattedDuration = useMemo(() => {
    const minutes = Math.floor(recordDuration / 60);
    let seconds = recordDuration;
    if (minutes > 0) {
      seconds = seconds - minutes * 60;
    }
    return `${(minutes > 9 ? '' : '0') + minutes}:${
      (seconds > 9 ? '' : '0') + seconds
    }`;
  }, [recordDuration]);

  const onStartRecording = () => {
    setIsRecording(true);
    if (props.onRecordingStart) {
      props.onRecordingStart();
    }
  };
  const onStopRecording = () => {
    setIsRecording(false);
    if (props.onRecordingStop) {
      props.onRecordingStop();
    }
  };
  const setAudioPlaying = () => {
    setPlaying(true);
  };
  const setAudioStop = () => {
    setPlaying(false);
  };
  const recorderControls = useVoiceVisualizer({
    onStartRecording,
    onStopRecording,

    onStartAudioPlayback: setAudioPlaying,
    onResumedAudioPlayback: setAudioPlaying,
    onEndAudioPlayback: setAudioStop,
    onPausedAudioPlayback: setAudioStop,
  });

  const {
    audioRef,
    recordedBlob,
    duration,
    formattedRecordingTime,
    isRecordingInProgress,
    clearCanvas,
    stopRecording,
    startRecording,
    error,
    togglePauseResume,
  } = recorderControls;
  const {Timer, startTimer, timerCompleted} = useComponentTimer({
    callback: startRecording,
  });

  // Get the recorded audio blob
  // useEffect(() => {
  // }, [recorderControls]);

  // Get the error when it occurs
  useEffect(() => {
    if (!error) return;

    console.error(error);
  }, [error]);

  const handleRestartClick = () => {
    // startTimer();
    resetComponent();
    if (props.onRestartClick) {
      props.onRestartClick();
    }
  };
  const handleFinishClick = () => {
    stopRecording();
    if (props.onFinishClick) {
      props.onFinishClick();
    }

    // Send jsFinish GA event
    if (isAppIimjobs || typeof location === 'undefined') {
      return;
    }

    handleTracking('jsFinish', {
      event_category: 'Round Zero',
      Origin: location?.pathname ?? '',
      Source: 'Resume',
      UserId: sessionStorage.getItem('userId') || 'NA',
      Device: sessionStorage.getItem('device') || 'desktop',
      Type: 'Audio',
    });
  };

  const handleUploadClick = () => {
    if (props.onUploadClick) {
      props.onUploadClick(recordedBlob);
    }

    if (isAppIimjobs || typeof location === 'undefined') {
      return;
    }

    handleTracking('jsUpload', {
      event_category: 'Round Zero',
      Origin: location?.pathname ?? '',
      Source: 'Resume',
      UserId: sessionStorage.getItem('userId') || 'NA',
      Device: sessionStorage.getItem('device') || 'desktop',
      Type: 'Audio',
    });
  };
  const togglePlayback = () => {
    togglePauseResume();
    // setPlaying(p => !p);
  };
  // const handl = ()=>{}
  useEffect(() => {
    if (isMinimalist && minimalistViewCallback) {
      minimalistViewCallback();
    }
    if (!isMinimalist && isRecording) {
      resetComponent();
    }
  }, [isMinimalist]);

  const resetComponent = () => {
    clearCanvas();
    setRecordDuration(0);
    setIsRecording(false);
  };
  const {origin} = getRefTagAndOrigin();

  const handleStartRecording = () => {
    if (!isAppIimjobs) {
      handleTracking('jsGuidelines', {
        Origin: `${origin}`,
        Source: 'Resume',
        UserId: sessionStorage.getItem('userId') || 'NA',
        event_category: 'Round Zero',
        Type: 'Audio',
      });
      handleTracking('jsPause', {
        Origin: `${origin}`,
        Source: 'Resume',
        UserId: sessionStorage.getItem('userId') || 'NA',
        event_category: 'Round Zero',
        Type: 'Audio',
      });
      handleTracking('jsPlay', {
        Origin: `${origin}`,
        Source: 'Resume',
        UserId: sessionStorage.getItem('userId') || 'NA',
        event_category: 'Round Zero',
        Type: 'Audio',
      });
      handleTracking('jsRerecord', {
        Origin: `${origin}`,
        Source: 'Resume',
        UserId: sessionStorage.getItem('userId') || 'NA',
        event_category: 'Round Zero',
        Type: 'Audio',
      });
    }
    if (isLarge) {
      startTimer();
    } else {
      setMinimalistViewCallback(startTimer);
      setMinimalistView('Round Zero- Record your Resume');
    }

    try {
      if (isAppIimjobs) {
        return;
      }

      handleTracking('jsStartRecording', {
        event_category: 'Round Zero',
        Origin: window?.location?.pathname ?? '',
        Source: 'Resume',
        UserId: sessionStorage.getItem('userId') || 'NA',
        Device: sessionStorage.getItem('device') || 'desktop',
        Type: 'Audio',
      });
    } catch (err) {
      console.error('Error occurred while starting the recording', err);
    }
  };

  const [RestartAlert, toggleRestartAlert] = useAlertCallback({
    primaryAction: 'Restart',
    secondaryAction: 'Cancel',
    subtitle: 'Alert : This will delete your current recording',
    title: 'Are you sure you want to restart?',
    primaryCallback: handleRestartClick,
  });
  return (
    <>
      <Box sx={{}}>
        <Box
          sx={{
            borderRadius: 3,
            border: '1px solid #7E7E7E33',
            width: '100%',
            // backgroundColor: 'gray.dark',
            background: '#7E7E7E33',
            position: 'relative',
          }}
        >
          <VoiceVisualizer
            height={
              isMinimalist
                ? `calc(100vh - ${
                    props?.source === 'onboarding' ? '420' : '215'
                  }px)`
                : isLarge
                  ? 378
                  : 178
            } // 380-2px because of border
            width={'100%'}
            mainBarColor={theme.palette.gray.dark}
            secondaryBarColor={theme.palette.gray.dark}
            gap={2}
            speed={0.5}
            fullscreen={true}
            isControlPanelShown={false}
            isDownloadAudioButtonShown={false}
            isDefaultUIShown={false}
            controls={recorderControls}
          />
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              height: '100%',
              bottom: 0,
              margin: 'auto',
              width: '100%',
              p: 2,
            }}
          >
            {Timer}
            {recordedBlob && (
              <IconContainer className="playIcon" onClick={togglePlayback}>
                {isPlaying ? <FaPause /> : <FaPlay />}
              </IconContainer>
            )}
            {props.isUploading && (
              <Box
                sx={{
                  position: 'absolute',
                  zIndex: 2,
                  bottom: 0,
                  left: 0,
                  width: '100%',
                }}
                px={2}
              >
                <Box textAlign={'right'}>
                  <Typography
                    fontSize={16}
                    lineHeight={'30px'}
                    fontWeight={600}
                    color={'#fff'}
                    sx={{
                      textShadow: '0px 3px 4px #00000070',
                    }}
                  >
                    Uploading
                  </Typography>
                </Box>
                <Box width={'100%'} mb={2}>
                  <LinearProgress
                    sx={{
                      height: 6,
                      borderRadius: 5,
                      backgroundColor: '#fff',
                      '.MuiLinearProgress-bar': {
                        backgroundColor: 'text.primary',
                      },
                    }}
                  />
                </Box>
              </Box>
            )}
            <Box position={'relative'} width={'100%'} height={'100%'}>
              {!isRecording && !recordedBlob && (
                <Image src={audioWave} fill alt="" />
              )}
            </Box>
          </Box>
          {!isPlaying && (
            <RecordingOverlay
              lapsedDuration={formattedDuration}
              totalDuration="01:00"
              isRecording={isRecording}
            />
          )}
        </Box>
        <Box
          display={'flex'}
          mt={props?.source === 'onboarding' ? 4.625 : 1.5}
          textAlign={'center'}
          gap={{xxs: 1.5, md: 2}}
          justifyContent={'center'}
        >
          {!isRecording && !recordedBlob ? (
            // {false ? (
            isAppIimjobs ? (
              <Button
                variant="contained"
                disabled={!timerCompleted || props.disabled}
                color="secondary"
                sx={{
                  backgroundColor: isAppIimjobs
                    ? 'secondary'
                    : theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: isAppIimjobs
                      ? 'secondary'
                      : theme.palette.primary.main,
                  },
                }}
                onClick={handleStartRecording}
              >
                <Image
                  src={audioIcon}
                  alt=""
                  width={16}
                  height={16}
                  style={{
                    filter:
                      !timerCompleted || props.disabled
                        ? 'brightness(3.2)'
                        : 'brightness(10)',
                  }}
                />{' '}
                &nbsp;&nbsp;Start Recording
              </Button>
            ) : (
              <Button
                variant="contained"
                disabled={!timerCompleted || props.disabled}
                color="secondary"
                sx={{
                  backgroundColor: isAppIimjobs
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main,
                  color: isAppIimjobs ? 'default' : 'white',
                  '&:hover': {
                    color: 'white',
                    backgroundColor: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                  },
                  borderRadius: 0.5,
                  borderColor: theme.palette.primary.main,
                }}
                size="large"
                onClick={handleStartRecording}
              >
                <Image
                  src={audioIcon}
                  alt=""
                  width={16}
                  height={16}
                  style={{
                    filter:
                      !timerCompleted || props.disabled
                        ? 'brightness(3.2)'
                        : 'brightness(10)',
                  }}
                />{' '}
                &nbsp;&nbsp;Start Recording
              </Button>
            )
          ) : (
            <>
              {recordedBlob ? (
                <>
                  {isAppIimjobs ? (
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={
                        props.isUploaded
                          ? toggleRestartAlert
                          : handleRestartClick
                      }
                      disabled={props.disabled}
                    >
                      <VscDebugRestart /> &nbsp;&nbsp;
                      {props?.source === 'onboarding'
                        ? 'Record Again'
                        : 'Restart'}
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={
                        props.isUploaded
                          ? toggleRestartAlert
                          : handleRestartClick
                      }
                      disabled={props.disabled}
                      sx={{
                        borderRadius: '4px',
                      }}
                    >
                      <VscDebugRestart /> &nbsp;&nbsp;
                      {props?.source === 'onboarding'
                        ? 'Record Again'
                        : 'Restart'}
                    </Button>
                  )}
                  {props.isUploaded && props?.source !== 'onboarding' ? (
                    <Typography
                      fontSize={{xxs: 14, md: 16}}
                      fontWeight={500}
                      display={'flex'}
                      justifyContent={'center'}
                      alignItems={'center'}
                      gap={1}
                    >
                      <Box
                        component={'img'}
                        src={SurveyCheckGreen}
                        width={{xxs: 26, md: 32}}
                        height={{xxs: 26, md: 32}}
                      />
                      {/* {isLarge ? 'Your Video resume is uploaded!' : 'Uploaded!'} */}
                      Uploaded
                    </Typography>
                  ) : isAppIimjobs ? (
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={handleUploadClick}
                      disabled={props.disabled}
                    >
                      <FiUpload />
                      &nbsp;&nbsp;Upload
                    </Button>
                  ) : (
                    <Button
                      color="secondary"
                      onClick={handleUploadClick}
                      disabled={props.disabled}
                      variant={isAppIimjobs ? 'contained' : 'outlined'}
                      sx={{
                        backgroundColor: isAppIimjobs
                          ? theme.palette.secondary.main
                          : theme.palette.primary.main,
                        color: isAppIimjobs ? 'default' : 'white',
                        '&:hover': {
                          color: 'white',
                          backgroundColor: theme.palette.primary.main,
                          borderColor: theme.palette.primary.main,
                        },
                        borderRadius: 0.5,
                        borderColor: theme.palette.primary.main,
                      }}
                      size="large"
                    >
                      <FiUpload />
                      &nbsp;&nbsp;Upload
                    </Button>
                  )}
                </>
              ) : isAppIimjobs ? (
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleFinishClick}
                  disabled={props.disabled || disableClick}
                >
                  <FaRegStopCircle />
                  &nbsp;&nbsp;Finish
                </Button>
              ) : (
                <Button
                  variant={isAppIimjobs ? 'contained' : 'outlined'}
                  sx={{
                    backgroundColor: isAppIimjobs
                      ? theme.palette.secondary.main
                      : theme.palette.primary.main,
                    color: isAppIimjobs ? 'default' : 'white',
                    '&:hover': {
                      color: 'white',
                      backgroundColor: theme.palette.primary.main,
                      borderColor: theme.palette.primary.main,
                    },
                    borderRadius: 0.5,
                    borderColor: theme.palette.primary.main,
                  }}
                  size="large"
                  color="secondary"
                  onClick={handleFinishClick}
                  disabled={props.disabled || disableClick}
                >
                  <FaRegStopCircle />
                  &nbsp;&nbsp;Finish
                </Button>
              )}
            </>
          )}
        </Box>
      </Box>
      <RestartAlert />
    </>
  );
};
export default AudioRecorder;
